import { useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { useInView } from 'react-intersection-observer';

import { amplitudeService, ScreenName } from '../../../../../shared/amplitude';
import { ViewReviewDetail } from '../../../../../shared/amplitude/events/review';
import { useAppSelector } from '../../../../../shared/store';

import COLOR from '../../../../../shared/constant/colorset';

import { useReviewList } from '../../hooks';
import { hiddenScrollBar } from '../../../../../shared/utils/hidden-scrollbar';
import { ProductReview } from '../../ProductReview.service';
import NextImage from '../../../../../shared/components/NextImage';
import { NoImage } from '../../../../../shared/images';

const Position = styled.div`
  position: relative;
`;

const Grid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(6, 121px);
  gap: 3px;
  grid-gap: 3px;
  max-height: 616px;
  overflow-x: hidden;
  overflow-y: auto;
  margin-right: -10px;
  ${hiddenScrollBar({ x: 'hidden', y: 'auto' })}
`;

const ImageCount = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 22px;
  height: 22px;
  border-top-left-radius: 4px;
  background-color: hsla(0, 0%, 0%, 0.4);

  span {
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    color: ${COLOR.kurlyWhite};
  }
`;

const Thumbnail = styled.div`
  position: relative;
  cursor: pointer;
  width: 120px;
  height: 120px;
  background: url(${NoImage}) no-repeat 50% 50%;
  background-size: 40%;
  background-color: ${COLOR.kurlyGray200};
`;

const RENDER_IMAGE_LENGTH = 36;

interface Props {
  contentsProductNo: number;
  onClickImage(index: number): void;
}

export default function ReviewModalImageList({ onClickImage, contentsProductNo }: Props) {
  const root = useRef<HTMLDivElement>(null);
  const [ref, inView] = useInView({
    root: root?.current,
    initialInView: false,
  });

  const productData = useAppSelector(({ productDetail }) => productDetail);
  const { data, status, fetchNextPage, hasNextPage, isFetchingNextPage } = useReviewList(contentsProductNo, {
    reviewType: 'IMAGE',
  });

  const reviewList = useMemo(() => (data ? data.pages.flatMap(({ reviews }) => reviews) : []), [data]);

  const handleClick = async (reviewData: ProductReview, index: number) => {
    onClickImage(index);
    await amplitudeService.setScreenName(ScreenName.PRODUCT_PHOTO_REVIEW_LIST);
    await amplitudeService.logEvent(
      new ViewReviewDetail({
        reviewData,
        sortType: 'RECENTLY',
        productData,
      }),
    );
    await amplitudeService.setScreenName(ScreenName.PRODUCT_REVIEW_DETAIL);
  };

  useEffect(() => {
    if (inView && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, isFetchingNextPage, hasNextPage]);

  const imageReviewList = useMemo(() => reviewList.filter((review) => review.images?.length > 0), [reviewList]);

  return status === 'success' && imageReviewList ? (
    <Position ref={root}>
      <Grid>
        {imageReviewList.map((review, index) => (
          <Thumbnail
            ref={index + 1 === reviewList.length ? ref : null}
            key={review.id}
            aria-hidden="true"
            tabIndex={0}
            onClick={() => handleClick(review, index)}
          >
            <NextImage
              width={120}
              height={120}
              src={review.images[0].url}
              alt={review.dealProductName}
              objectFit="cover"
              loading="lazy"
            />
            {review.images.length > 1 && (
              <ImageCount>
                <span>{review.images.length}</span>
              </ImageCount>
            )}
          </Thumbnail>
        ))}
        {isFetchingNextPage &&
          Array.from({ length: RENDER_IMAGE_LENGTH }, (_, index) => <Thumbnail key={`skeleton-${index}`} />)}
      </Grid>
    </Position>
  ) : null;
}
